import { useEffect, useRef } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { AlertTriangleIcon, CheckIcon, CloseIcon } from '@xantory/icons';
import { styles } from './toast.styles';
import { useT } from '@xantory/i18n';
export type ToastVariant = 'success' | 'warning' | 'danger' | 'info';
export interface ToastItem {
  id: string;
  message: string;
  variant?: ToastVariant;
  duration?: number;
}
interface ToastProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}
const icoStyle = {
  success: styles.icoSuccess,
  info: styles.icoSuccess,
  warning: styles.icoWarn,
  danger: styles.icoDanger
} as const;
function Toast({
  item,
  onDismiss
}: {
  item: ToastItem;
  onDismiss: (id: string) => void;
}) {
  const t = useT('ui');
  const variant = item.variant ?? 'success';
  const timer = useRef(0);

  // Auto-dismiss, paused while the pointer is over the toast (so it can be read
  // and the dismiss button reached without racing the timeout).
  const arm = () => {
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => onDismiss(item.id), item.duration ?? 4000);
  };
  useEffect(() => {
    arm();
    return () => clearTimeout(timer.current);
    // eslint intentionally absent in this repo; deps cover the reset cases.
  }, [item.id, item.duration]);
  return <div
  // Danger/warning interrupt (alert = assertive); success/info announce politely.
  role={variant === 'danger' || variant === 'warning' ? 'alert' : 'status'} onMouseEnter={() => clearTimeout(timer.current)} onMouseLeave={arm} {...stylex.props(styles.toast)}>
      <span {...stylex.props(styles.ico, icoStyle[variant])}>
        {variant === 'warning' || variant === 'danger' ? <AlertTriangleIcon size={16} /> : <CheckIcon size={16} strokeWidth={2.2} />}
      </span>
      <span {...stylex.props(styles.message)}>{item.message}</span>
      <button type="button" aria-label={t('close')} onClick={() => onDismiss(item.id)} {...stylex.props(styles.closeBtn)}>
        <CloseIcon size={14} />
      </button>
    </div>;
}
export function ToastProvider({
  toasts,
  onDismiss
}: ToastProps) {
  if (toasts.length === 0) return null;
  return <div {...stylex.props(styles.container)}>
      {toasts.map(t => <Toast key={t.id} item={t} onDismiss={onDismiss} />)}
    </div>;
}